import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { PromptConfig, PromptSetApiService, DoraFieldConfig } from '../../shared/api.service';
import { TranslateModule } from '@ngx-translate/core';

export interface SummaryPromptDialogData {
    promptSetId: string;
    promptSetName: string;
    outputSchemaFields: DoraFieldConfig[];
    existingSummaryPrompt?: any;
}

@Component({
    selector: 'app-summary-prompt-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatCheckboxModule,
        DragDropModule,
        TranslateModule
    ],
    templateUrl: './summary-prompt-dialog.component.html',
    styleUrl: './summary-prompt-dialog.component.scss'
})
export class SummaryPromptDialogComponent implements OnInit {
    summaryForm: FormGroup;
    isSubmitting = false;
    isDeleting = false;
    availableFields: DoraFieldConfig[] = [];
    hasExistingSummaryPrompt: boolean = false;

    private readonly defaultSummaryContent =
        'Return one JSON object only, containing the keys "short_answer" and "summary". Set "short_answer" to either "yes", "no", or "to be checked". Populate "summary" with your summary.\n\n**SUMMARIES**: \n\n${summaries}';

    constructor(
        private fb: FormBuilder,
        private apiService: PromptSetApiService,
        private dialogRef: MatDialogRef<SummaryPromptDialogComponent>,
        private snackBar: MatSnackBar,
        @Inject(MAT_DIALOG_DATA) public data: SummaryPromptDialogData
    ) {
        this.hasExistingSummaryPrompt = !!data.existingSummaryPrompt;

        // Initialize form with existing data or defaults
        const existingPrompt = data.existingSummaryPrompt;
        const existingMetadata = existingPrompt?.promptMetadataInformation?.[0];
        const existingConfig = existingPrompt?.promptConfiguration?.[0];
        const existingContent = existingConfig?.prompt?.content?.[0]?.prompt_content || '';
        const contentValue = existingContent.trim().length > 0 ? existingContent : this.defaultSummaryContent;
        const existingSelectedFields = existingMetadata?.metadataDict?.summary_input_fields || [];

        // Reorder availableFields based on existing order if present
        if (existingSelectedFields.length > 0) {
            const orderedFields: DoraFieldConfig[] = [];
            const unselectedFields: DoraFieldConfig[] = [];
            
            // Add selected fields in the order they appear in existingSelectedFields
            existingSelectedFields.forEach((fieldName: string) => {
                const field = data.outputSchemaFields.find(f => f.field_name === fieldName);
                if (field) {
                    orderedFields.push(field);
                }
            });
            
            // Add remaining unselected fields
            data.outputSchemaFields.forEach(field => {
                if (!existingSelectedFields.includes(field.field_name)) {
                    unselectedFields.push(field);
                }
            });
            
            this.availableFields = [...orderedFields, ...unselectedFields];
        } else {
            this.availableFields = data.outputSchemaFields || [];
        }

        this.summaryForm = this.fb.group({
            name: [existingMetadata?.name || 'Summary Prompt', Validators.required],
            context: [existingConfig?.prompt?.context || '', Validators.required],
            content: [contentValue, Validators.required],
            selectedFields: this.fb.array(
                this.availableFields.map(field => 
                    this.fb.control(existingSelectedFields.includes(field.field_name))
                )
            )
        });
    }

    ngOnInit(): void {
        // Ensure at least one field is selected
        this.summaryForm.get('selectedFields')?.valueChanges.subscribe((values: boolean[]) => {
            const hasSelection = values && values.some(v => v);
            if (!hasSelection) {
                this.summaryForm.get('selectedFields')?.setErrors({ noSelection: true });
            } else {
                this.summaryForm.get('selectedFields')?.setErrors(null);
            }
        });
        
        // Initial validation check
        const initialValues = this.selectedFieldsArray.value;
        const hasInitialSelection = initialValues && initialValues.some((v: boolean) => v);
        if (!hasInitialSelection) {
            this.summaryForm.get('selectedFields')?.setErrors({ noSelection: true });
        }
    }

    get selectedFieldsArray(): FormArray {
        return this.summaryForm.get('selectedFields') as FormArray;
    }

    getSelectedFieldNames(): string[] {
        const selectedFields: string[] = [];
        const values = this.selectedFieldsArray.value;
        
        // Return fields in the order they appear in availableFields (which can be reordered)
        values.forEach((isSelected: boolean, index: number) => {
            if (isSelected && this.availableFields[index]) {
                selectedFields.push(this.availableFields[index].field_name);
            }
        });
        
        return selectedFields;
    }

    /**
     * Handle drag and drop reordering of fields
     */
    onFieldDrop(event: CdkDragDrop<DoraFieldConfig[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        // Reorder the availableFields array
        moveItemInArray(this.availableFields, event.previousIndex, event.currentIndex);
        
        // Get current selection states
        const currentValues = this.selectedFieldsArray.value;
        
        // Reorder the form array to match
        const newValues = [...currentValues];
        moveItemInArray(newValues, event.previousIndex, event.currentIndex);
        
        // Update the form array
        this.selectedFieldsArray.clear();
        newValues.forEach(value => {
            this.selectedFieldsArray.push(this.fb.control(value));
        });
        
        // Revalidate after rebuilding the form array
        const hasSelection = newValues.some(v => v);
        if (!hasSelection) {
            this.selectedFieldsArray.setErrors({ noSelection: true });
        } else {
            this.selectedFieldsArray.setErrors(null);
        }
    }

    generateSummaryPromptId(): string {
        // Remove dashes from prompt_set_id and prefix with 'summary_'
        return `summary_${this.data.promptSetId.replace(/-/g, '')}`;
    }

    saveSummaryPrompt(): void {
        if (this.summaryForm.invalid) {
            this.snackBar.open('Please fill all required fields and select at least one input field', 'Close', {
                duration: 5000,
                horizontalPosition: 'end',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
            });
            return;
        }

        const selectedFields = this.getSelectedFieldNames();
        if (selectedFields.length === 0) {
            this.snackBar.open('Please select at least one input field', 'Close', {
                duration: 5000,
                horizontalPosition: 'end',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
            });
            return;
        }

        this.isSubmitting = true;
        const formValue = this.summaryForm.value;
        const summaryPromptId = this.generateSummaryPromptId();

        // Construct the prompt configuration payload
        const promptConfig: PromptConfig = {
            prompt_metadata_information: [{
                prompt_set_id: this.data.promptSetId,
                prompt_identifier: summaryPromptId,
                name: formValue.name,
                description: 'Summary prompt for aggregating analysis results',
                subscription_id: '', // Will be set by backend
                metadataDict: {
                    summary_input_fields: selectedFields
                }
            }],
            prompt_configuration: [{
                promptIdentifier: summaryPromptId,
                validationIdentifier: summaryPromptId,
                prompt: {
                    context: formValue.context,
                    content: [{
                        prompt_content: formValue.content,
                        defaultReplacement: [],
                        removeIfNotSet: false
                    }],
                    examples: [],
                    response_format: '',
                    use_json_mode: true
                },
                isPrivate: false
            }]
        };

        this.apiService.storePrompt(promptConfig).subscribe({
            next: (response) => {
                this.isSubmitting = false;
                this.snackBar.open('Summary prompt saved successfully', 'Close', {
                    duration: 3000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['success-snackbar']
                });
                this.dialogRef.close(response);
            },
            error: (error) => {
                this.isSubmitting = false;
                console.error('Error saving summary prompt:', error);
                this.snackBar.open('Error saving summary prompt', 'Close', {
                    duration: 5000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar']
                });
            }
        });
    }

    deleteSummaryPrompt(): void {
        this.isDeleting = true;
        const summaryPromptId = this.generateSummaryPromptId();
        this.apiService.deletePrompt({ promptIdentifier: summaryPromptId }).subscribe({
            next: () => {
                this.isDeleting = false;
                this.snackBar.open('Summary prompt deleted successfully', 'Close', {
                    duration: 3000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['success-snackbar']
                });
                this.dialogRef.close({ deleted: true });
            },
            error: (error: any) => {
                this.isDeleting = false;
                console.error('Error deleting summary prompt:', error);
                this.snackBar.open('Error deleting summary prompt', 'Close', {
                    duration: 5000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar']
                });
            }
        });
    }

    cancel(): void {
        this.dialogRef.close();
    }
}
